import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, Building2, Users } from 'lucide-react';
import { PageHeader } from '../../components/layout';
import {
    Card, CardBody, Button, Modal, ConfirmDialog, toast, EmptyState
} from '../../components/common';
import { departmentApi } from '../../services/api';
import DepartmentForm from './DepartmentForm';
import './Departments.css';

export default function DepartmentList() {
    const queryClient = useQueryClient();
    const [showForm, setShowForm] = useState(false);
    const [editingDept, setEditingDept] = useState(null);
    const [deleteDept, setDeleteDept] = useState(null);

    const { data, isLoading } = useQuery({
        queryKey: ['departments'],
        queryFn: () => departmentApi.list(),
        select: (res) => res.data?.data || []
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => departmentApi.delete(id),
        onSuccess: () => {
            toast.success('Department deleted successfully');
            queryClient.invalidateQueries(['departments']);
            setDeleteDept(null);
        },
        onError: () => {
            toast.error('Failed to delete department');
        }
    });

    const handleEdit = (dept) => {
        setEditingDept(dept);
        setShowForm(true);
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setEditingDept(null);
    };

    return (
        <div className="departments-page">
            <PageHeader
                title="Department Management"
                subtitle="Manage organizational departments"
                breadcrumbs={[
                    { label: 'Settings' },
                    { label: 'Departments', path: '/departments' }
                ]}
                actions={
                    <Button icon={Plus} onClick={() => setShowForm(true)}>
                        Add Department
                    </Button>
                }
            />

            <Card>
                <CardBody>
                    {isLoading ? (
                        <div className="departments-grid">
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} className="department-card skeleton" />
                            ))}
                        </div>
                    ) : data?.length === 0 ? (
                        <EmptyState
                            icon={Building2}
                            title="No Departments"
                            description="Get started by creating your first department"
                            action={
                                <Button icon={Plus} onClick={() => setShowForm(true)}>
                                    Add Department
                                </Button>
                            }
                        />
                    ) : (
                        <div className="departments-grid">
                            {data?.map((dept) => (
                                <div key={dept.id} className="department-card">
                                    <div className="dept-icon">
                                        <Building2 size={24} />
                                    </div>
                                    <div className="dept-content">
                                        <h4>{dept.name}</h4>
                                        <p className="dept-code">{dept.code}</p>
                                        <p className="dept-desc">{dept.description}</p>
                                        <div className="dept-meta">
                                            <span><Users size={14} /> {dept.userCount} users</span>
                                        </div>
                                    </div>
                                    <div className="dept-actions">
                                        <button onClick={() => handleEdit(dept)} title="Edit">
                                            <Edit2 size={16} />
                                        </button>
                                        <button onClick={() => setDeleteDept(dept)} title="Delete" className="danger">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardBody>
            </Card>

            <Modal
                isOpen={showForm}
                onClose={handleCloseForm}
                title={editingDept ? 'Edit Department' : 'Add Department'}
                size="md"
            >
                <DepartmentForm
                    department={editingDept}
                    onSuccess={() => {
                        handleCloseForm();
                        queryClient.invalidateQueries(['departments']);
                    }}
                    onCancel={handleCloseForm}
                />
            </Modal>

            <ConfirmDialog
                isOpen={!!deleteDept}
                onClose={() => setDeleteDept(null)}
                onConfirm={() => deleteMutation.mutate(deleteDept?.id)}
                title="Delete Department"
                message={`Are you sure you want to delete "${deleteDept?.name}"? Users in this department will need to be reassigned.`}
                confirmText="Delete"
                variant="danger"
                loading={deleteMutation.isPending}
            />
        </div>
    );
}
